<script setup lang="ts">
import { computed, ref } from 'vue';
import { PasswordResetService } from '@/services/password-reset.service';
import { PopUpType } from '@/models/pop-up-type.enum';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import LoggedOutFrame from '@/layouts/LoggedOutFrame.vue';
import NotificationMessage from '@/components/common/NotificationMessage.vue';
import BackstageInput from '@/components/common/BackstageInput.vue';
import PasswordComplexityRule from '@/components/login/PasswordComplexityRule.vue';

const route = useRoute();
const router = useRouter();
const { t } = useI18n();

const password = ref('');
const notification = ref('');
const confirmPassword = ref('');
const passwordResetService = new PasswordResetService();
const error = ref<string | undefined>(undefined);
const isLoading = ref(false);

const username = computed(() => {
  return route.query.username as string;
});

const passwordLongEnough = computed(() => {
  return password.value.length >= 8;
});

const passwordHasOneLetter = computed(() => {
  return /[a-zA-Z]/.test(password.value);
});

const passwordHasOneNumber = computed(() => {
  return /[0-9]/.test(password.value);
});

const passwordsMatch = computed(() => {
  return !!password.value && password.value === confirmPassword.value;
});

const isSubmitButtonDisabled = computed(() => {
  return (
    !passwordLongEnough.value ||
    !passwordHasOneLetter.value ||
    !passwordHasOneNumber.value ||
    !passwordsMatch.value ||
    isLoading.value
  );
});

const resetPassword = async (event: Event) => {
  event.preventDefault();
  isLoading.value = true;
  error.value = undefined;

  try {
    const response = await passwordResetService.setPassword(
      username.value,
      password.value,
      route.params.token as string,
    );

    notification.value = t(response).toString();

    router.push('/login');
  } catch (e: any) {
    error.value = t(e.message).toString();
  }

  isLoading.value = false;
};
</script>

<template>
  <LoggedOutFrame
    :header="$t('createPassword.header')"
    :background-image-url="$t('createPassword.background_image_url')">
    <form class="form" @submit.prevent="resetPassword">
      <div v-if="error" class="error">{{ error }}</div>

      <i18n-t keypath="createPassword.form_header" tag="p" class="form-header" scope="global">
        <template v-slot:username>
          <span class="username">{{ username }}</span>
        </template>
      </i18n-t>

      <div class="password-complexity-rules">
        <div class="password-complexity-rule">
          <PasswordComplexityRule
            :label="$t('createPassword.password_rule_length')"
            :password-matches="passwordLongEnough" />
        </div>

        <div class="password-complexity-rule">
          <PasswordComplexityRule
            :label="$t('createPassword.password_rule_letters')"
            :password-matches="passwordHasOneLetter" />
        </div>

        <div class="password-complexity-rule">
          <PasswordComplexityRule
            :label="$t('createPassword.password_rule_numbers')"
            :password-matches="passwordHasOneNumber" />
        </div>
      </div>

      <BackstageInput
        v-model="password"
        class="password-input"
        :label="$t('createPassword.password')"
        type="password"
        data-cy="create-password-field" />

      <BackstageInput
        v-model="confirmPassword"
        class="password-input"
        :label="$t('createPassword.confirm_password')"
        type="password"
        data-cy="confirm-password-field" />

      <div class="buttons-cnt">
        <input
          type="submit"
          class="button submit-button"
          :value="$t('createPassword.button_label')"
          :disabled="isSubmitButtonDisabled" />
      </div>
    </form>
    <NotificationMessage v-if="notification" :type="PopUpType.Success" v-on:close="notification = ''">
      {{ $t(notification) }}
    </NotificationMessage>
  </LoggedOutFrame>
</template>

<style lang="scss" scoped>
.form-header {
  border-bottom: 1px solid #cacaca;
  padding-bottom: 1rem;
  margin-bottom: 1.5rem;
}

.submit-button {
  width: 100%;
}

.username {
  color: #9b9b9b;
}

.password-complexity-rule {
  margin-bottom: 1rem;
}

.password-complexity-rules {
  margin-bottom: 1.5rem;
}

.error {
  color: var(--scarlet-red-color);
}

.password-input {
  display: flex;
  margin-bottom: 20px;
}
</style>
